/**
 * A React hook that uses ResizeObserver to track element dimensions.
 */

import { useEffect, useRef, useState } from 'react';

interface Dimensions {
  width: number;
  height: number;
}

/**
 * Hook to observe and track element dimensions using ResizeObserver.
 * @returns A tuple containing the ref to attach to the element and the current dimensions.
 */
export function useResizeObserver<T extends HTMLElement = HTMLDivElement>(): [React.RefObject<T | null>, Dimensions] {
  const ref = useRef<T | null>(null);
  const [dimensions, setDimensions] = useState<Dimensions>({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({ width, height });
      }
    });

    resizeObserver.observe(element);

    // Get initial dimensions
    const { width, height } = element.getBoundingClientRect();
    setDimensions({ width, height });

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return [ref, dimensions];
}
